import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface CertificationRow {
  readonly releaseCertificationId: string;
  readonly certificationCode: string;
  readonly title: string;
  readonly releaseRef: string | null;
  readonly isMajor: boolean;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'VALIDATING', 'CERTIFIED', 'APPROVED', 'PUBLISHED', 'REJECTED'];
const MAJOR = ['', 'true', 'false'];

/**
 * M34 — Release Certification Dashboard (§31 / §21 / §22). Register of release
 * certifications (T459) across the draft→validating→certified→approved→published
 * lifecycle with the six quality gates and the major-release flag (BR-06
 * escalation). Supports raising a certification and driving it — validate,
 * certify, approve, publish, reject and delete — each wired to the guarded
 * /v1/qms/certifications write API (approve + publish carry the segregated
 * CERTIFICATION_APPROVE / CERTIFICATION_PUBLISH permissions, §22/§30 three-way
 * SoD). Filterable by status and major-release flag.
 */
export function ReleaseCertificationDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<CertificationRow[]>([]);
  const [status, setStatus] = useState('');
  const [isMajor, setIsMajor] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newRelease, setNewRelease] = useState('');
  const [newMajor, setNewMajor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (isMajor) params.set('isMajor', isMajor);
    const qs = params.toString();
    apiRequest<{ data: CertificationRow[] }>(`/v1/qms/certifications${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, isMajor, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m34.cert.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/certifications`, {
        method: 'POST',
        body: { title: newTitle.trim(), releaseRef: newRelease.trim() || undefined, isMajor: newMajor },
        token,
      });
      setNotice(t('m34.cert.created', { title: newTitle.trim() }));
      setNewTitle('');
      setNewRelease('');
      setNewMajor(false);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_CERTIFICATION_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: CertificationRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/certifications/${r.releaseCertificationId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m34.cert.notice', { code: r.certificationCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  const remove = async (r: CertificationRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/certifications/${r.releaseCertificationId}`, { method: 'DELETE', token });
      setNotice(t('m34.cert.notice', { code: r.certificationCode, label: t('m34.cert.act.deleted') }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_CERTIFICATION_MANAGE'));
    }
  };

  return (
    <main aria-label="certification-dashboard">
      <h1>{t('m34.cert.title')}</h1>

      <section aria-label="create-certification">
        <h2>{t('m34.cert.createHeading')}</h2>
        <form aria-label="certification-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.common.title')}<input aria-label="field-cert-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m34.common.release')}<input aria-label="field-cert-release" value={newRelease} onChange={(e) => setNewRelease(e.target.value)} /></label>
          <label>{t('m34.cert.major')}<input type="checkbox" aria-label="field-cert-major" checked={newMajor} onChange={(e) => setNewMajor(e.target.checked)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-certification" disabled={busy}>{busy ? t('m34.cert.creating') : t('m34.cert.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="certification-action-error">{actionError}</p>}
        {notice && <p aria-label="certification-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.cert.releaseClass')}
          <select aria-label="filter-major" value={isMajor} onChange={(e) => setIsMajor(e.target.value)}>
            {MAJOR.map((s) => <option key={s || 'all'} value={s}>{s === 'true' ? t('m34.cert.optMajor') : s === 'false' ? t('m34.cert.optMinor') : t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="certification-count">{t('m34.cert.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="certification-table"
            rows={rows}
            rowKey={(r) => r.releaseCertificationId}
            columns={[
              { key: 'certificationCode', header: t('m34.common.code'), render: (r) => r.certificationCode },
              { key: 'title', header: t('m34.common.title'), render: (r) => r.title },
              { key: 'releaseRef', header: t('m34.common.release'), render: (r) => r.releaseRef ?? '—' },
              { key: 'isMajor', header: t('m34.cert.colClass'), render: (r) => r.isMajor ? t('m34.cert.optMajor') : t('m34.cert.optMinor') },
              { key: 'status', header: t('m34.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m34.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`validate-${r.certificationCode}`} onClick={() => void act(r, 'validate', t('m34.cert.act.validating'), 'M34_CERTIFICATION_MANAGE')}>{t('m34.cert.btnValidate')}</Button>
                  )}
                  {r.status === 'VALIDATING' && (
                    <Button variant="secondary" type="button" aria-label={`certify-${r.certificationCode}`} onClick={() => void act(r, 'certify', t('m34.cert.act.certified'), 'M34_CERTIFICATION_MANAGE')}>{t('m34.cert.btnCertify')}</Button>
                  )}
                  {r.status === 'CERTIFIED' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`approve-${r.certificationCode}`} onClick={() => void act(r, 'approve', t('m34.cert.act.approved'), 'M34_CERTIFICATION_APPROVE', { approverLevel: 'RELEASE_MANAGER' })}>{t('m34.cert.btnApprove')}</Button>
                      <Button variant="danger" type="button" aria-label={`reject-${r.certificationCode}`} onClick={() => void act(r, 'reject', t('m34.cert.act.rejected'), 'M34_CERTIFICATION_APPROVE', { reason: 'Rejected via console' })}>{t('m34.cert.btnReject')}</Button>
                    </>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.certificationCode}`} onClick={() => void act(r, 'publish', t('m34.cert.act.published'), 'M34_CERTIFICATION_PUBLISH')}>{t('m34.cert.btnPublish')}</Button>
                  )}
                  {!['PUBLISHED', 'REJECTED'].includes(r.status) && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.certificationCode}`} onClick={() => void remove(r)}>{t('m34.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
